import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { ROUTES, STORAGE_KEYS } from '../config';
import { canonicalizeLoginIdentifier } from './loginIdentifier';
import { AuthAPI } from './api';
// New JWT-only helper that lives at src/api/auth.js — see that file for
// the rationale (subdomain-aware axios client, refresh-token interceptor).
// The existing AuthAPI (session-aware /login/ endpoint) is still used as
// the primary login path because it returns the user record alongside the
// JWT pair in one round-trip; we fall back to api/auth.js' pure-JWT
// `login()` only if the session endpoint is unavailable (e.g. on a tenant
// where the session-cookie variant is intentionally disabled).
import { login as jwtLogin } from '../api/auth';
import { buildLoginExtras, shouldFallbackToJwtLogin } from './loginSecurity';
import {
  clearLocalAuth,
  markSignedIn,
  markSignedOut,
  readRefreshToken,
  readStoredUser,
  writeStoredUser,
  writeTokens,
} from './authStorage';


const AuthContext = createContext(null);

/**
 * Auth provider — handles login / logout, persists JWT + user, and exposes
 * the current user to the rest of the app via `useAuth()`.
 *
 * The Django backend supports BOTH JWT and the existing session cookie, so
 * a user who has just logged into the HTML site will already be considered
 * authenticated when the React app loads — `bootstrap()` calls `/me/` to
 * detect that case.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => readStoredUser());
  const [bootstrapping, setBootstrapping] = useState(true);

  // On first load, ask the server "who am I?" — works via cookie or JWT.
  // A cached ``taskydo.user`` is NOT enough: after Logout (or an expired
  // Bearer token) /me/ 401s, and if we kept that cache LoginPage would
  // think the user was still signed in and bounce them to /task.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const applyMe = (me) => {
        if (cancelled) return;
        setUser(me);
        writeStoredUser(me);
      };
      const becomeAnonymous = () => {
        if (cancelled) return;
        clearLocalAuth();
        setUser(null);
      };
      try {
        applyMe(await AuthAPI.me());
      } catch {
        // An expired/invalid Authorization header makes DRF 401 before
        // it falls through to the session cookie. Drop the JWTs and
        // probe once more so a still-valid Django session can restore.
        const hadAccess = (() => {
          try {
            return !!localStorage.getItem(STORAGE_KEYS.ACCESS);
          } catch {
            return false;
          }
        })();
        if (hadAccess) {
          try {
            localStorage.removeItem(STORAGE_KEYS.ACCESS);
            localStorage.removeItem(STORAGE_KEYS.REFRESH);
          } catch {
            // ignore
          }
          try {
            applyMe(await AuthAPI.me());
            return;
          } catch {
            // still unauthenticated
          }
        }
        becomeAnonymous();
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email, password) => {
    const extras = await buildLoginExtras();
    const identifier = canonicalizeLoginIdentifier(email);
    // Primary path: the /login/ endpoint sets the Django session cookie AND
    // returns JWTs + the user record in one round-trip — best of both worlds.
    let data;
    try {
      data = await AuthAPI.login(identifier, password, extras);
    } catch (sessionErr) {
      // Fallback to the pure-JWT helper at src/api/auth.js only when the
      // session-cookie /login/ endpoint is missing (404/405), blocked by
      // CSRF (403), or never reached. Auth/security denials (401/429)
      // must not be retried — that was surfacing as
      // ``HTTP 429 • POST /super_admin/token/`` after a device-id reject.
      if (!shouldFallbackToJwtLogin(sessionErr)) {
        throw sessionErr;
      }
      const jwt = await jwtLogin(identifier, password, extras);
      data = { access: jwt.access, refresh: jwt.refresh, user: null };

      // We don't have a user record from this endpoint — fetch it via /me/.
      try {
        const me = await AuthAPI.me();
        data.user = me;
      } catch (_) {
        // /me/ failed too — keep going; the rest of the app guards on `user`.
      }
    }

    markSignedIn();
    writeTokens({ access: data.access, refresh: data.refresh });
    if (data.user) writeStoredUser(data.user);
    setUser(data.user || null);
    return data.user;
  }, []);


  const signup = useCallback(async ({ firstName, lastName, email, password, passwordConfirmation }) => {
    const extras = await buildLoginExtras();
    const data = await AuthAPI.publicSignup({
      first_name: firstName,
      last_name: lastName,
      email: String(email || '').trim().toLowerCase(),
      password,
      password_confirmation: passwordConfirmation,
      ...extras,
    });
    markSignedIn();
    writeTokens({ access: data.access, refresh: data.refresh });
    if (data.user) writeStoredUser(data.user);
    setUser(data.user || null);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    // Wipe client creds FIRST so in-flight 401 handlers cannot rebuild
    // a half-logged-in state, then flush the Django session / blacklist
    // the refresh token, then hard-nav to /login so a leftover cookie
    // cannot bounce the SPA back onto /task via LoginPage.
    const refresh = readRefreshToken();
    markSignedOut();
    setUser(null);
    try {
      await Promise.all([
        AuthAPI.logout(),
        refresh ? AuthAPI.blacklistRefresh(refresh) : Promise.resolve(),
      ]);
    } catch {
      // Best-effort — local state is already anonymous.
    }
    if (typeof window !== 'undefined' && window.location) {
      window.location.replace(ROUTES.LOGIN);
    }
  }, []);

  const value = useMemo(
    () => ({ user, bootstrapping, isAuthenticated: !!user, login, signup, logout, setUser }),
    [user, bootstrapping, login, signup, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
